import Layout from '../Layout';
import DeleteConfirmModal from './DeleteConfirmModal';
import NotificationListRow from './NotificationListRow';
import TrashIcon from './TrashIcon';
import {
  FILTER_TABS,
  FILTER_OPTIONS,
  FOOTER_NOTE,
  formatDetailDate,
} from './notificationInboxConstants';

const NotificationInboxToolbar = ({
  busy,
  selectMode,
  checkedCount,
  filterOpen,
  onFilterToggle,
  onFilterPick,
  onMarkAllRead,
  onToggleSelect,
  onDeleteAll,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-6">
    <div>
      <h2 className="text-lg font-bold text-brand-dark-blue">All Notifications</h2>
      <p className="text-sm text-gray-500">
        Select a notification on the left to view full details on the right.
      </p>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <button
          type="button"
          onClick={onFilterToggle}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Filter ▾
        </button>
        {filterOpen && (
          <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onFilterPick(opt.id)}
                className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onMarkAllRead}
        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        Mark all as read
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={onToggleSelect}
        className={`rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50 ${
          selectMode
            ? 'border-brand-medium-blue bg-brand-light-blue/30 text-brand-dark-blue'
            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        {selectMode ? 'Cancel' : 'Select'}
      </button>
      <button
        type="button"
        disabled={busy || (selectMode && checkedCount === 0)}
        onClick={onDeleteAll}
        className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
      >
        {selectMode ? `Delete Selected (${checkedCount})` : 'Delete All*'}
      </button>
    </div>
  </div>
);

const NotificationInboxList = ({
  filtered,
  activeTab,
  selectMode,
  selected,
  checkedIds,
  onTabChange,
  onSelect,
  onToggleCheck,
  onDelete,
}) => (
  <div className="border-b border-gray-200 lg:w-[42%] lg:border-b-0 lg:border-r">
    <div className="flex gap-2 overflow-x-auto border-b border-gray-100 px-4 py-3">
      {FILTER_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onTabChange(tab.id)}
          className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium ${
            activeTab === tab.id
              ? 'bg-brand-medium-blue text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
    <div className="max-h-[480px] overflow-y-auto">
      {filtered.length === 0 ? (
        <p className="px-4 py-8 text-sm text-gray-500">No notifications in this view.</p>
      ) : (
        filtered.map((n) => (
          <NotificationListRow
            key={n.id}
            item={n}
            isActive={selected?.id === n.id}
            selectMode={selectMode}
            isChecked={checkedIds.has(n.id)}
            onToggleCheck={onToggleCheck}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        ))
      )}
    </div>
  </div>
);

export const NotificationInboxShell = ({
  subtitle,
  inbox,
  renderDetailActions,
  getDetailBody,
  getDetailHeading,
}) => {
  const {
    loading,
    error,
    selected,
    activeTab,
    setActiveTab,
    filterOpen,
    setFilterOpen,
    setFilterValue,
    busy,
    selectMode,
    checkedIds,
    deletePrompt,
    filtered,
    toggleSelectMode,
    toggleChecked,
    requestDeleteOne,
    requestDeleteBulk,
    performDelete,
    cancelDelete,
    handleSelect,
    handleMarkAllRead,
  } = inbox;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-brand-dark-blue">Notifications</h1>
          <p className="mt-2 text-gray-600">{subtitle}</p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <NotificationInboxToolbar
            busy={busy}
            selectMode={selectMode}
            checkedCount={checkedIds.size}
            filterOpen={filterOpen}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            onFilterPick={(id) => {
              setFilterValue(id);
              setActiveTab(id);
              setFilterOpen(false);
            }}
            onMarkAllRead={handleMarkAllRead}
            onToggleSelect={toggleSelectMode}
            onDeleteAll={requestDeleteBulk}
          />
          <p className="px-6 pb-2 text-xs text-gray-500">*Except Tutor Scene system messages.</p>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-brand-medium-blue" />
            </div>
          ) : error ? (
            <p className="px-6 py-8 text-red-600">{error}</p>
          ) : (
            <div className="flex min-h-[520px] flex-col lg:flex-row">
              <NotificationInboxList
                filtered={filtered}
                activeTab={activeTab}
                selectMode={selectMode}
                selected={selected}
                checkedIds={checkedIds}
                onTabChange={(id) => {
                  setActiveTab(id);
                  setFilterValue(id);
                }}
                onSelect={handleSelect}
                onToggleCheck={toggleChecked}
                onDelete={requestDeleteOne}
              />

              <div className="flex-1 p-6">
                {!selected ? (
                  <p className="text-sm text-gray-500">Select a notification to view details.</p>
                ) : (
                  <>
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-2xl font-bold text-brand-dark-blue">{selected.title}</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {formatDetailDate(selected.createdAt)}
                          {selected.relativeTime ? ` • ${selected.relativeTime}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!selected.isRead && (
                          <span className="mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-red-500" />
                        )}
                        {!selected.isSystem && (
                          <button
                            type="button"
                            onClick={() => requestDeleteOne(selected)}
                            className="rounded p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            aria-label="Delete notification"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                      <p className="mb-2 font-semibold text-gray-900">
                        {getDetailHeading?.(selected) || selected.title}
                      </p>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                        {getDetailBody?.(selected) || selected.preview}
                      </p>
                    </div>
                    {renderDetailActions?.(selected)}
                    <p className="mt-8 text-xs text-gray-500">{FOOTER_NOTE}</p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmModal
        open={Boolean(deletePrompt)}
        title={deletePrompt?.title}
        message={deletePrompt?.message}
        busy={busy}
        onConfirm={performDelete}
        onCancel={cancelDelete}
      />
    </Layout>
  );
};

export default NotificationInboxShell;
